import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter, catchError } from 'rxjs/operators';
import { Subscription, of, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '@core/services/api.service';
import { HeaderService } from '@core/services/header.service';
import { AuthService } from '@core/services/auth.service';
import { PermissionService } from '@core/services/permission.service';
import { AttendanceTimerService } from '@core/services/attendance-timer.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { Memo } from '../../shared/utils/memo';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

@Component({
  standalone: false,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  // Phase 1: getter memoization — see shared/utils/memo.ts
  private memo = new Memo();

  employees: any[] = [];
  departments: any[] = [];
  attendance: any[] = [];
  leaves: any[] = [];
  announcements: any[] = [];
  leaveBalances: any[] = [];
  teams: any[] = [];
  teamMembersMap: { [teamId: string]: any[] } = {};
  shifts: any[] = [];
  rosterAssignments: any[] = [];
  clients: any[] = [];
  loading = true;
  
  userRole = 'employee';
  currentUser: any = {};
  currentUserId = '';
  workTimerDisplay = '00:00:00';
  liveClockDisplay = '00:00:00';
  today = new Date();
  newThisMonth = 0;
  checkingIn = false;
  checkingOut = false;
  isLateCheckIn = false;
  isCheckedIn = false;
  myShift: any = null;

  companySettings: any = {
    gracePeriodMinutes: 15,
    defaultShiftStart: '09:00',
    defaultShiftEnd: '18:00',
    lateThresholdTime: '09:15'
  };
  private workTimerInterval: any;
  private liveClockInterval: any;
  private routeSub?: Subscription;

  myAttendance: any = null;
  allTodayAttendance: any[] = [];
  allAttendance: any[] = [];
  myLeaves: any[] = [];
  calendarWeeks: any[][] = [];
  calendarMonth = '';
  calendarDate = new Date();
  calSelectedDate: string | null = null;
  calSelectedRecord: any = null;
  calSelectedStatus: string = '';
  calCheckInLocation  = '';
  calCheckOutLocation = '';
  
  weeklyData: number[] = [0, 0, 0, 0, 0, 0, 0];

  showTeamDialog = false;
  teamForm: any = { id: '', name: '', teamLeadId: '', departmentId: '' };
  editingTeam = false;

  managerWeeklyRaw: { date: Date, dateStr: string, records: any[] }[] = [];
  monthlyAttendance: any[] = [];
  managerTeamMemberIds: string[] = [];
  holidays: { [key: string]: string } = {};

  payrollThisMonth: string = '₹0';
  payrollChangeText: string = '';

  myManager: any = null;

  showEmployeeModal = false;
  modalEmployee: any = null;
isHoliday(dateStr: string): boolean {
  return !!this.holidays[dateStr];
}
  isActiveEmp = (e: any) => {
    const s = (e?.status || '').toLowerCase();
    const r = (e?.role || '').toLowerCase().replace(/^role_/, '');
    return s !== 'inactive';
  };
  get activeEmployees(): any[] { return this.employees.filter(this.isActiveEmp); }
  get totalEmployees(): number { return this.activeEmployees.length; }

  isEmployeeActiveById = (id: string): boolean => {
    const emp = this.employees.find(e => e.id === id || e.employeeId === id || e.userId === id);
    return !!emp && (emp.status || '').toLowerCase() !== 'inactive';
  };

  get distinctAttendance(): any[] {
    const map = new Map<string, any>();
    for (const a of this.attendance) {
      if (!a.employeeId) continue;
      const existing = map.get(a.employeeId);
      if (!existing) {
        map.set(a.employeeId, a);
      } else if (a.checkIn && existing.checkIn) {
        if (this.normalizeTime(a.checkIn) < this.normalizeTime(existing.checkIn)) {
          map.set(a.employeeId, a);
        }
      }
    }
    return Array.from(map.values());
  }

  private get lateThresholdMinutes(): number {
    const t = this.companySettings?.lateThresholdTime ?? '09:15';
    const p = t.split(':');
    return (+p[0]) * 60 + (+p[1] || 0);
  }

  get presentToday(): number {
    return this.distinctAttendance.filter(a => a.checkIn || a.status === 'present').length;
  }
  get onLeaveToday(): number { return this.leaves.filter(l => l.status === 'approved' && this.isLeaveToday(l)).length; }
  get pendingLeaveCount(): number { return this.leaves.filter(l => l.status === 'pending' && this.isEmployeeActiveById(l.employeeId)).length; }
  get lateArrivals(): number {
    return this.distinctAttendance.filter(a => {
      if (!a.checkIn) return false;
      const t = this.normalizeTime(a.checkIn);
      const parts = t.split(':');
      return (+parts[0]) * 60 + (+parts[1] || 0) > this.lateThresholdMinutes;
    }).length;
  }
  get absentToday(): number { return Math.max(0, this.totalEmployees - this.presentToday - this.onLeaveToday); }
  get attendanceRate(): number { return this.totalEmployees > 0 ? Math.round((this.presentToday / this.totalEmployees) * 100) : 0; }

  empNameFn = (e: any) => e ? `${e.firstName || ''} ${e.lastName || ''}`.trim() : '';

  get userFirstName(): string { return this.currentUser.firstName || 'User'; }

  get myEmployeeRecord(): any {
    return this.employees.find((e: any) => e.id === this.currentUser?.id || e.employeeId === this.currentUserId) || this.currentUser;
  }
  get myClientName(): string {
    const cid = this.myEmployeeRecord?.clientId;
    return cid ? (this.clients.find((c: any) => c.id === cid)?.name || '') : '';
  }
  get myProjectName(): string { return this.myEmployeeRecord?.project || ''; }
  get myAllocations(): { client: string; project: string }[] {
    const raw = this.myEmployeeRecord?.rolesResponsibilities;
    const out: { client: string; project: string }[] = [];
    try {
      const arr = raw ? JSON.parse(raw) : [];
      (Array.isArray(arr) ? arr : []).forEach((r: any) => {
        const client = r.clientName || this.clients.find((c: any) => c.id === r.clientId)?.name || '';
        if (client || r.project) out.push({ client, project: r.project || '' });
      });
    } catch { }
    if (!out.length && (this.myClientName || this.myProjectName)) {
      out.push({ client: this.myClientName, project: this.myProjectName });
    }
    return out;
  }
  get myWorkMode(): string { return this.myEmployeeRecord?.workType || this.myEmployeeRecord?.location || ''; }
  get myShiftLabel(): string {
    if (!this.myShift) return '';
    return `${this.myShift.name} · ${this.normalizeTime(this.myShift.startTime)}–${this.normalizeTime(this.myShift.endTime)}`;
  }
  get userInitials(): string {
    return ((this.currentUser.firstName?.charAt(0) || '') + (this.currentUser.lastName?.charAt(0) || '')).toUpperCase();
  }

  get greetingPeriod(): 'morning' | 'afternoon' | 'evening' | 'night' {
    const h = new Date().getHours();
    if (h >= 6 && h < 12) return 'morning';
    if (h >= 12 && h < 17) return 'afternoon';
    if (h >= 17 && h < 21) return 'evening';
    return 'night';
  }
  get greetingLabel(): string {
    return { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening', night: 'Good night' }[this.greetingPeriod];
  }
  get greetingScene() {
    const mr = 'rgba(51,65,85,';
    const scenes: Record<string, any> = {
      morning: {
        skyTop:'#f9d580', skyMid:'#f5aa6a', skyBot:'#e8845a',
        cardBg:'#5a1a14',
        lumX:382, lumY:55, lumCore:'#FFE878', lumH1R:18, lumH2R:30, lumH3R:56,
        lumH1:'#FFEE9A', lumH1Op:0.32, lumH2:'#FFD060', lumH2Op:0.20, lumH3Op:0.18,
        raysOp:0.30, raysColor:'#FFE090', moonOp:0,
        horizonColor:'#FFB870', horizonOp:0.24,
        cloudsAOp:0.55, cloudsBOp:0.38, cloudsCOp:0.28,
        cloudsATint:'#fff0d8', cloudsBTint:'#ffe0c0', cloudsCTint:'#ffd8b0',
        mtnFarColor:'#e0a890', mtnFarOp:0.20, mtnMidColor:'#c07860', mtnMidOp:0.28,
        mtnNearColor:'#8b3020', mtnNearOp:0.42, hillsColor:'#6e2018', hillsOp:0.55,
        treesOp:0.38, birdsAOp:0.60, birdsBOp:0.32, starsOp:0,
        chipBg:'rgba(51,65,85,0.18)', chipColor:'#ffa080',
        avBg:'#8b1a10', avColor:'#ffddd8',
        stickerBg:'rgba(51,65,85,0.16)', stickerColor:'#ffaa90',
        stickers:['Rise & lead','Own your morning','Start strong','Chase the goal','Make it count'],
        quotes:[
          {text:'The secret of getting ahead is getting started.',author:'Mark Twain'},
          {text:'Each morning we are born again. What we do today matters most.',author:'Buddha'},
          {text:'Believe you can and you are halfway there.',author:'Theodore Roosevelt'},
          {text:"Your limitation — it's only your imagination.",author:'Unknown'},
          {text:'Push yourself, because no one else is going to do it for you.',author:'Unknown'}
        ]
      },
      afternoon: {
        skyTop:'#1a70c0', skyMid:'#2e90d6', skyBot:'#82ccf0',
        cardBg:'#581410',
        lumX:382, lumY:20, lumCore:'#FFFCE0', lumH1R:16, lumH2R:24, lumH3R:44,
        lumH1:'#FFF8C0', lumH1Op:0.35, lumH2:'#FFEE80', lumH2Op:0.18, lumH3Op:0.14,
        raysOp:0.20, raysColor:'#FFF8C8', moonOp:0,
        horizonColor:'#a8dcf8', horizonOp:0.18,
        cloudsAOp:0.78, cloudsBOp:0.60, cloudsCOp:0.44,
        cloudsATint:'#ffffff', cloudsBTint:'#f8feff', cloudsCTint:'#f0faff',
        mtnFarColor:'#8090b0', mtnFarOp:0.18, mtnMidColor:'#607090', mtnMidOp:0.24,
        mtnNearColor:'#8b3020', mtnNearOp:0.38, hillsColor:'#6e2018', hillsOp:0.52,
        treesOp:0.40, birdsAOp:0.65, birdsBOp:0.38, starsOp:0,
        chipBg:'rgba(51,65,85,0.18)', chipColor:'#ffb0a0',
        avBg:'#7a1412', avColor:'#ffe0d8',
        stickerBg:'rgba(51,65,85,0.16)', stickerColor:'#ffb8a8',
        stickers:['Stay in the zone','Deep work mode','Halfway there','Keep the pace','Afternoon grind'],
        quotes:[
          {text:'Focus on being productive instead of just being busy.',author:'Tim Ferriss'},
          {text:"Don't watch the clock; do what it does. Keep going.",author:'Sam Levenson'},
          {text:'The only way to do great work is to love what you do.',author:'Steve Jobs'},
          {text:'Success is not final; failure is not fatal. The courage to continue counts.',author:'Winston Churchill'},
          {text:"It always seems impossible until it's done.",author:'Nelson Mandela'}
        ]
      },
      evening: {
        skyTop:'#1e0828', skyMid:'#8a2050', skyBot:'#ee6030',
        cardBg:'#501018',
        lumX:382, lumY:115, lumCore:'#FF8848', lumH1R:22, lumH2R:38, lumH3R:64,
        lumH1:'#FF6828', lumH1Op:0.28, lumH2:'#FF5010', lumH2Op:0.16, lumH3Op:0.20,
        raysOp:0.18, raysColor:'#FF9860', moonOp:0,
        horizonColor:'#FF7040', horizonOp:0.32,
        cloudsAOp:0.65, cloudsBOp:0.50, cloudsCOp:0.38,
        cloudsATint:'#ffc090', cloudsBTint:'#ff9870', cloudsCTint:'#ffb080',
        mtnFarColor:'#901840', mtnFarOp:0.30, mtnMidColor:'#6e1028', mtnMidOp:0.42,
        mtnNearColor:'#500818', mtnNearOp:0.58, hillsColor:'#3a0510', hillsOp:0.68,
        treesOp:0.45, birdsAOp:0.40, birdsBOp:0.20, starsOp:0,
        chipBg:'rgba(51,65,85,0.22)', chipColor:'#ffaa80',
        avBg:'#6e1010', avColor:'#ffd8c8',
        stickerBg:'rgba(51,65,85,0.18)', stickerColor:'#ffb090',
        stickers:['Wind down well','Reflect & recharge','Golden hour','Wrap it up strong','Rest is productive'],
        quotes:[
          {text:'Great things are done by a series of small things brought together.',author:'Vincent Van Gogh'},
          {text:'At the end of the day, let there be no excuses, no regrets.',author:'Steve Maraboli'},
          {text:'Take rest; a field that has rested gives a bountiful crop.',author:'Ovid'},
          {text:'The evening knows what the morning never suspected.',author:'Robert Frost'},
          {text:'An evening of reflection is worth a morning of planning.',author:'Unknown'}
        ]
      },
      night: {
        skyTop:'#060a18', skyMid:'#0c1438', skyBot:'#141e50',
        cardBg:'#3e0e16',
        lumX:368, lumY:32, lumCore:'#e8eeff', lumH1R:14, lumH2R:20, lumH3R:36,
        lumH1:'#c8d4ff', lumH1Op:0.20, lumH2:'#a8b8f0', lumH2Op:0.12, lumH3Op:0.10,
        raysOp:0, raysColor:'#ffffff', moonOp:1,
        horizonColor:'#182880', horizonOp:0.12,
        cloudsAOp:0.12, cloudsBOp:0.08, cloudsCOp:0.06,
        cloudsATint:'#4850a0', cloudsBTint:'#404880', cloudsCTint:'#383870',
        mtnFarColor:'#1c1440', mtnFarOp:0.60, mtnMidColor:'#120e2e', mtnMidOp:0.75,
        mtnNearColor:'#500818', mtnNearOp:0.70, hillsColor:'#3e0610', hillsOp:0.82,
        treesOp:0.55, birdsAOp:0, birdsBOp:0, starsOp:1,
        chipBg:'rgba(51,65,85,0.22)', chipColor:'#e8c0c0',
        avBg:'#5a0e18', avColor:'#f5d8d8',
        stickerBg:'rgba(51,65,85,0.18)', stickerColor:'#e8c8c8',
        stickers:['Rest. Lead tomorrow','Recharge mode on','Stars are watching','Sleep builds leaders','Tomorrow is yours'],
        quotes:[
          {text:"Rest when you're weary. Refresh and renew yourself, your mind.",author:'Ralph Marston'},
          {text:'A good laugh and a long sleep are the best cures.',author:'Irish Proverb'},
          {text:'Sleep is the best meditation.',author:'Dalai Lama'},
          {text:'The night is always darkest before the dawn.',author:'Thomas Fuller'},
          {text:'Each night I die; each morning I am reborn.',author:'Mahatma Gandhi'}
        ]
      }
    };
    return scenes[this.greetingPeriod];
  }
  greetingQuoteIdx = Math.floor(Math.random() * 5);
  get greetingQuote() { return this.greetingScene.quotes[this.greetingQuoteIdx]; }
  get greetingSticker() { return this.greetingScene.stickers[this.greetingQuoteIdx]; }

  // The SVG nature scene binds ~40 attributes to this palette. Cached in a field (refreshed
  // when the period flips, checked by the existing 1s clock tick) instead of a getter, so
  // change detection doesn't rebuild the scene object on every tick.
  scene: any = null;
  private scenePeriod = '';

  private refreshSceneIfNeeded(): void {
    const p = this.greetingPeriod;
    if (p !== this.scenePeriod) {
      this.scenePeriod = p;
      this.scene = this.greetingScene;
    }
  }

  nextGreetingQuote(): void {
    this.greetingQuoteIdx = (this.greetingQuoteIdx + 1) % 5;
    this.scheduleNextQuote();
  }

  /** Light accent palette per time of day — deliberately NOT the dark navy/gold card from the
   * reference design; this stays on our existing white card and just tints the icon, progress
   * bar and corner glow, the same way the rest of the app uses a single accent color per state. */
  get greetingAccent(): {
    icon: string; color: string; chipBg: string; glowFrom: string;
    haloMid: string; haloIn: string; coreColor: string; skyWash: string;
  } {
    const accents: Record<string, any> = {
      // Layered like real light falloff: a soft outer glow -> a mid halo -> a tight bright
      // ring -> a solid core — not one flat radial blob. skyWash is a very faint full-card
      // tint so the time-of-day mood reads across the whole card, not just the corner.
      morning: {
        icon: 'wb_sunny', color: '#c8790a', chipBg: 'rgba(250,190,90,0.16)',
        glowFrom: 'radial-gradient(circle, rgba(250,190,90,0.22) 0%, rgba(250,190,90,0) 70%)',
        haloMid:  'radial-gradient(circle, rgba(250,175,70,0.32) 0%, rgba(250,175,70,0) 72%)',
        haloIn:   'radial-gradient(circle, rgba(255,205,110,0.55) 0%, rgba(255,205,110,0) 75%)',
        coreColor:'radial-gradient(circle, #ffe6ab 0%, #ffd27a 55%, rgba(255,210,120,0) 100%)',
        skyWash:  'linear-gradient(100deg, transparent 35%, rgba(250,190,90,0.07) 100%)',
      },
      afternoon: {
        icon: 'wb_twilight', color: '#2b7fd4', chipBg: 'rgba(90,170,250,0.13)',
        glowFrom: 'radial-gradient(circle, rgba(90,170,250,0.16) 0%, rgba(90,170,250,0) 70%)',
        haloMid:  'radial-gradient(circle, rgba(100,180,250,0.26) 0%, rgba(100,180,250,0) 72%)',
        haloIn:   'radial-gradient(circle, rgba(170,215,255,0.55) 0%, rgba(170,215,255,0) 75%)',
        coreColor:'radial-gradient(circle, #fffdf0 0%, #e8f4ff 55%, rgba(232,244,255,0) 100%)',
        skyWash:  'linear-gradient(100deg, transparent 35%, rgba(90,170,250,0.06) 100%)',
      },
      evening: {
        icon: 'wb_twilight', color: '#d1611f', chipBg: 'rgba(255,140,80,0.16)',
        glowFrom: 'radial-gradient(circle, rgba(255,140,80,0.22) 0%, rgba(255,140,80,0) 70%)',
        haloMid:  'radial-gradient(circle, rgba(255,120,60,0.32) 0%, rgba(255,120,60,0) 72%)',
        haloIn:   'radial-gradient(circle, rgba(255,150,90,0.6) 0%, rgba(255,150,90,0) 75%)',
        coreColor:'radial-gradient(circle, #ffcf9e 0%, #ff9d5c 55%, rgba(255,157,92,0) 100%)',
        skyWash:  'linear-gradient(100deg, transparent 30%, rgba(255,120,60,0.09) 100%)',
      },
      night: {
        icon: 'nights_stay', color: '#5b56c9', chipBg: 'rgba(130,120,230,0.14)',
        glowFrom: 'radial-gradient(circle, rgba(130,120,230,0.14) 0%, rgba(130,120,230,0) 70%)',
        haloMid:  'radial-gradient(circle, rgba(140,130,235,0.22) 0%, rgba(140,130,235,0) 72%)',
        haloIn:   'radial-gradient(circle, rgba(200,195,245,0.5) 0%, rgba(200,195,245,0) 75%)',
        coreColor:'radial-gradient(circle, #f0eeff 0%, #cfc9f5 55%, rgba(207,201,245,0) 100%)',
        skyWash:  'linear-gradient(100deg, transparent 30%, rgba(120,110,220,0.08) 100%)',
      },
    };
    return accents[this.greetingPeriod];
  }

  /** Live clock formatted for the greeting card (h:mm AM/PM) — reuses the same tick that already
   * drives liveClockDisplay, no extra interval needed. */
  get greetingClock(): string {
    const now = new Date();
    let h = now.getHours();
    const m = this.pad(now.getMinutes());
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  }

  private quoteTimer: any;
  private readonly quoteDurationMs = 180000; // 3 minutes
  progressRunning = false;

  private scheduleNextQuote(): void {
    if (this.quoteTimer) clearTimeout(this.quoteTimer);
    this.quoteTimer = setTimeout(() => this.nextGreetingQuote(), this.quoteDurationMs);
    this.restartProgressBar();
  }

  private restartProgressBar(): void {
    this.progressRunning = false;
    setTimeout(() => { this.progressRunning = true; }, 20);
  }

  get avatarInitials(): string {
    const f = this.currentUser?.firstName?.[0] ?? '';
    const l = this.currentUser?.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }
  get currentDateTag(): string {
    return new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  get pendingLeavesList(): any[] { return this.leaves.filter(l => l.status === 'pending' && this.isEmployeeActiveById(l.employeeId)).slice(0, 5); }
  get approvedLeavesCount(): number { return this.leaves.filter(l => l.status === 'approved').length; }
  get deptNames(): string { return this.departments.slice(0, 3).map(d => d.name).join(' · ') + (this.departments.length > 3 ? '...' : ''); }

  get newJoiners(): any[] {
    const thisMonth = new Date().getMonth();
    const thisYear = new Date().getFullYear();
    return this.activeEmployees.filter((e: any) => {
      if (!e.dateOfJoining) return false;
      const d = new Date(e.dateOfJoining);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });
  }

  /** Zoho-migrated employees (creationSource === 'bulk_upload') never went through NovarcPulse's
   * own candidate → onboarding flow, so they have no real checklist and never will — showing
   * them in Onboarding Progress just means every row says "No checklist" for people HR never
   * expects to onboard here. Only employees who actually onboarded through this system belong
   * in this widget. */
  get newJoinersOnboardedHere(): any[] {
    return this.newJoiners.filter((e: any) => e.creationSource !== 'bulk_upload');
  }

  get lateAttendance(): any[] {
    return this.distinctAttendance.filter(a => {
      if (!a.checkIn) return false;
      const t = this.normalizeTime(a.checkIn);
      const parts = t.split(':');
      const h = +parts[0]; const m = +parts[1] || 0;
      return (h > 9 || (h === 9 && m > 15));
    });
  }

  get presentArc(): number { return this.totalEmployees > 0 ? (this.presentToday / this.totalEmployees) * 226 : 0; }
  get leaveArc(): number { return this.totalEmployees > 0 ? (this.onLeaveToday / this.totalEmployees) * 226 : 0; }

  wishedEmployees = new Set<string>();

  constructor(
    private apiService: ApiService,
    private headerService: HeaderService,
    private authService: AuthService,
    private router: Router,
    public permissions: PermissionService,
    public timerService: AttendanceTimerService,
    private http: HttpClient,
    private snackbarService: SnackbarService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Overview');
    this.currentUser = this.authService.getCurrentUser() || {};
    this.userRole = (this.authService.getUserRole() || 'employee').toLowerCase();
    this.currentUserId = this.currentUser.employeeId || this.currentUser.id || '';
    this.buildCalendar();
    this.loadDashboardData();
    this.loadMyManager();
    this.loadPayrollThisMonth();
    this.startLiveClock();
    this.scheduleNextQuote();


    this.routeSub = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e) => {
      if (e.urlAfterRedirects === '/overview' || e.urlAfterRedirects === '/dashboard') {
        this.loadDashboardData();
      }
    });

  this.timerService.display$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(d => this.workTimerDisplay = d);

  this.timerService.session$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(s => {
    this.isCheckedIn = s.isCheckedIn;
  });

  this.headerService.stopTimer$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.stopWorkTimer());
  }

  ngOnDestroy(): void {
    if (this.workTimerInterval) clearInterval(this.workTimerInterval);
    if (this.liveClockInterval) clearInterval(this.liveClockInterval);
    if (this.quoteTimer) clearTimeout(this.quoteTimer);
    if (this.routeSub) this.routeSub.unsubscribe();
  }

  isEmployee(): boolean { const r = this.userRole.toLowerCase(); return r === 'employee' || r === 'role_employee'; }
  isManager(): boolean { const r = this.userRole.toLowerCase(); return r === 'manager' || r === 'role_manager'; }
  isAdmin(): boolean {
    const r = this.userRole.toLowerCase().replace(/^role_/, '');
    return ['admin', 'super_admin', 'hr_admin', 'ceo', 'coo'].includes(r);
  }
  isHR(): boolean { const r = this.userRole.toLowerCase(); return r === 'hr' || r === 'hr_admin' || r === 'role_hr'; }

  normalizeTime(time: any): string {
    if (!time) return '';
    if (Array.isArray(time)) {
      return `${this.pad(time[0] ?? 0)}:${this.pad(time[1] ?? 0)}:${this.pad(time[2] ?? 0)}`;
    }
    return String(time).split('.')[0];
  }

  formatTime12(time: string): string {
    if (!time) return '';
    const parts = time.split(':');
    let h = +parts[0]; const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12; else if (h > 12) h -= 12;
    return `${this.pad(h)}:${m} ${ampm}`;
  }

  formatUtcTime12(time: any, _date?: any): string {
    if (!time) return '—';
    return this.formatTime12(this.normalizeTime(time));
  }

  private pad(n: number): string { return n < 10 ? '0' + n : '' + n; }

  private startLiveClock(): void {
    this.updateLiveClock();
    this.liveClockInterval = setInterval(() => this.updateLiveClock(), 1000);
  }

  private updateLiveClock(): void {
    const now = new Date();
    this.liveClockDisplay = now.toTimeString().split(' ')[0];
    // Repaints the greeting scene when the time-of-day period flips (morning/afternoon/…)
    this.refreshSceneIfNeeded();
  }

  private startWorkTimer(): void {
    if (this.workTimerInterval) clearInterval(this.workTimerInterval);
    this.updateWorkTimer();
    if (this.myAttendance?.checkIn) {
      this.workTimerInterval = setInterval(() => this.updateWorkTimer(), 1000);
    }
  }

  private updateWorkTimer(): void {
    if (this.allTodayAttendance.length === 0) { 
      this.workTimerDisplay = '00:00:00'; 
      return; 
    }
    
    let totalSeconds = 0;
    const now = new Date();
    
    for (const rec of this.allTodayAttendance) {
      if (!rec.checkIn) continue;
      
      const ciStr = this.normalizeTime(rec.checkIn);
      const ciParts = ciStr.split(':');
      const ci = new Date();
      ci.setHours(+ciParts[0], +ciParts[1] || 0, +ciParts[2] || 0, 0);
      
      let co: Date;
      if (rec.checkOut) {
        const coStr = this.normalizeTime(rec.checkOut);
        const coParts = coStr.split(':');
        co = new Date();
        co.setHours(+coParts[0], +coParts[1] || 0, +coParts[2] || 0, 0);
      } else {
        co = now;
      }
      
      totalSeconds += Math.max(0, Math.floor((co.getTime() - ci.getTime()) / 1000));
    }
    
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    this.workTimerDisplay = `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
    
    const hasOpenSession = this.allTodayAttendance.some(rec => rec.checkIn && !rec.checkOut);
    if (!hasOpenSession && this.workTimerInterval) {
      clearInterval(this.workTimerInterval);
      this.workTimerInterval = null;
    }
  }

  get todayStr(): string { return this.localDateStr(new Date()); }

  private localDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private extractList(res: any): any[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (res.data && Array.isArray(res.data)) return res.data;
    if (res.content && Array.isArray(res.content)) return res.content;
    return [];
  }











  loadDashboardData(): void {
    this.loading = true;
    const todayStr = this.localDateStr(new Date());
    forkJoin({
      employees: this.apiService.get('/employees').pipe(catchError(() => of([]))),
      departments: this.apiService.get('/departments').pipe(catchError(() => of([]))),
      attendance: this.apiService.get('/attendance?date=' + todayStr).pipe(catchError(() =>
        this.apiService.get('/attendance/today').pipe(catchError(() => of([])))
      )),
      leaves: this.apiService.get('/leaves').pipe(catchError(() => of([]))),
      announcements: this.apiService.get('/announcements').pipe(catchError(() => of([]))),
      balances: this.apiService.get('/leave-balances').pipe(catchError(() => of([]))),
      teams: this.apiService.get('/teams').pipe(catchError(() => of([]))),
      shifts: this.apiService.get('/shifts').pipe(catchError(() => of([]))),
      roster: this.apiService.get('/roster').pipe(catchError(() => of([]))),
      holidays: this.apiService.get('/holidays').pipe(catchError(() => of([]))),
      payroll: this.apiService.get('/payroll').pipe(catchError(() => of([]))),
      clients: this.apiService.get('/clients').pipe(catchError(() => of([]))),
      companySettings: this.apiService.get('/company-settings').pipe(catchError(() => of(null))),
      // Real onboarding checklist tasks (HR/Admin only — 403s harmlessly for other roles since
      // they never render the Onboarding Progress widget). Replaces the old fake heuristic that
      // scored "progress" off five unrelated Employee fields instead of actual task completion.
      onboardingTasks: this.apiService.get('/onboarding').pipe(catchError(() => of({ data: [] })))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.employees = this.extractList(data.employees);
        this.clients = this.extractList(data.clients);
        if (!this.myManager) this.resolveManagerFromList();
        this.departments = this.extractList(data.departments);
        this.attendance = this.extractList(data.attendance);
        this.leaves = this.extractList(data.leaves);
        this.announcements = this.extractList(data.announcements).slice(0, 5);
        this.leaveBalances = this.extractList(data.balances);
        this.teams = this.extractList(data.teams);
        this.shifts = this.extractList(data.shifts);
        this.rosterAssignments = this.extractList(data.roster);
        this.holidays = {};
        this.extractList(data.holidays).forEach((h: any) => {
          this.holidays[h.date] = h.name;
        });

        this.onboardingTasksByEmployee.clear();
        for (const t of this.extractList(data.onboardingTasks)) {
          const list = this.onboardingTasksByEmployee.get(t.employeeId) ?? [];
          list.push(t);
          this.onboardingTasksByEmployee.set(t.employeeId, list);
        }
        if (data.companySettings) {
          const cs = data.companySettings.data ?? data.companySettings;
          if (cs && typeof cs === 'object') {
            this.companySettings = {
              ...this.companySettings,
              gracePeriodMinutes: cs.gracePeriodMinutes ?? this.companySettings.gracePeriodMinutes,
              defaultShiftStart:  cs.defaultShiftStart  ?? this.companySettings.defaultShiftStart,
              defaultShiftEnd:    cs.defaultShiftEnd    ?? this.companySettings.defaultShiftEnd,
              lateThresholdTime:  cs.lateThresholdTime  ?? this.companySettings.lateThresholdTime,
            };
          }
        }
        const assignment = this.rosterAssignments.find((r: any) =>
          r.employeeId === this.currentUserId ||
          r.employeeId === this.currentUser.id
        );
        if (assignment) {
          this.myShift = this.shifts.find((s: any) => s.id === assignment.shiftId);
        }

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        this.newThisMonth = this.employees.filter(this.isActiveEmp).filter((e: any) => {
          const dateStr = e.dateOfJoining || e.createdAt;
          if (!dateStr) return false;
          return new Date(dateStr) >= thirtyDaysAgo;
        }).length;

        const todayStr = this.localDateStr(new Date());
        this.allTodayAttendance = this.attendance.filter((a: any) => {
          const attDate = a.date?.split('T')[0] || a.date;
          return attDate === todayStr && (
            a.employeeId === this.currentUserId ||
            a.employeeId === this.currentUser.id ||
            a.employeeId === this.currentUser.employeeId
          );
        });
        
        this.myAttendance = this.allTodayAttendance.length > 0 
          ? this.allTodayAttendance[this.allTodayAttendance.length - 1] 
          : null;
        
        if (this.myAttendance?.checkIn && this.myShift) {
          this.isLateCheckIn = this.checkIfLate(this.myAttendance.checkIn, this.myShift.startTime);
        }
        this.myLeaves = this.leaves.filter((l: any) =>
          l.employeeId === this.currentUserId ||
          l.employeeId === this.currentUser.id ||
          l.employeeId === this.currentUser.employeeId
        );
        const hasOpenToday = this.allTodayAttendance.some((r: any) => r.checkIn && !r.checkOut);
        if (hasOpenToday || this.allTodayAttendance.length > 0) {
          if (!hasOpenToday) {
            const yesterdayStr = this.localDateStr(new Date(Date.now() - 86400000));
            this.apiService.get('/attendance?date=' + yesterdayStr).pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((yData: any) => {
              const yRecords = (this.extractList(yData) as any[]).filter((r: any) =>
                (r.employeeId === this.currentUserId || r.employeeId === this.currentUser.id) &&
                r.checkIn && !r.checkOut
              );
              this.timerService.hydrateFromRecords([...yRecords, ...this.allTodayAttendance]);
            });
          } else {
            this.timerService.hydrateFromRecords(this.allTodayAttendance);
          }
        } else {
          const yesterdayStr = this.localDateStr(new Date(Date.now() - 86400000));
          this.apiService.get('/attendance?date=' + yesterdayStr).pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((yData: any) => {
            const yRecords = (this.extractList(yData) as any[]).filter((r: any) =>
              (r.employeeId === this.currentUserId || r.employeeId === this.currentUser.id) &&
              r.checkIn && !r.checkOut
            );
            this.timerService.hydrateFromRecords(yRecords);
          });
        }
        this.buildCalendar();
        this.loadWeeklyAttendance();
        this.loadMonthlyAttendance();
        if (this.isManager()) { this.loadManagerWeeklyData(); this.loadManagerTeamMembers(); this.loadTeamRegularisations(); }
        this.loadAllTeamMembers();
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  loadWeeklyAttendance(): void {
    const now = new Date();
    const dow = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
    const calls = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      return this.apiService.get('/attendance?date=' + dateStr).pipe(catchError(() => of([])));
    });
    forkJoin(calls).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (results: any[]) => {
        const total = this.totalEmployees || 1;
        this.weeklyData = results.map((res: any) => {
          const recs = this.extractList(res);
          if (!recs || recs.length === 0) return 0;
          const distinctPresent = new Set(
            recs.filter((a: any) => a.checkIn || a.status === 'present').map((a: any) => a.employeeId)
          ).size;
          return Math.min(100, Math.round((distinctPresent / total) * 100));
        });
      },
      error: () => {}
    });
  }

  loadPayrollThisMonth(): void {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  this.apiService.get(`/payroll/month/${month}/year/${year}`)
    .pipe(catchError(() => of([])))
    .pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      const payrolls = this.extractList(res);

      const total = payrolls.reduce((sum: number, p: any) => {
        return sum + (p.netSalary || p.salary || p.amount || 0);
      }, 0);

      this.payrollThisMonth = this.formatToIndianCurrency(total);
    });
}

formatToIndianCurrency(amount: number): string {
  if (!amount) return '₹0';

  if (amount >= 10000000) {
    return '₹' + (amount / 10000000).toFixed(2) + 'Cr';
  } else if (amount >= 100000) {
    return '₹' + (amount / 100000).toFixed(1) + 'L';
  } else {
    return '₹' + amount.toLocaleString('en-IN');
  }
}





loadMonthlyAttendance(): void {
  this.apiService.get('/attendance/my')
    .pipe(catchError(() => of([])))
    .pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.monthlyAttendance = this.extractList(res);
      this.buildCalendar();
    });
}

  loadManagerWeeklyData(): void {
    const now = new Date();
    const dow = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
    const days = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
    const calls = days.map(d =>
      this.apiService.get('/attendance?date=' + this.localDateStr(d)).pipe(catchError(() => of([])))
    );
    forkJoin(calls).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (results: any[]) => {
        this.managerWeeklyRaw = days.map((d, i) => ({
          date: d,
          dateStr: this.localDateStr(d),
          records: this.extractList(results[i])
        }));
      },
      error: () => {}
    });
  }

  loadManagerTeamMembers(): void {
    this.apiService.get('/employees/my-team').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.managerTeamMemberIds = this.extractList(res)
          .map((e: any) => e.id)
          .filter(Boolean);
      },
      error: () => {}
    });
  }

  get myTeam(): any {
    return this.teams.find((t: any) => t.teamLeadId === this.currentUserId || t.teamLeadId === this.currentUser.id) || null;
  }

  trackByMember = (_: number, s: any): string => s?.member?.id ?? s?.id ?? String(_);

  get myTeams(): any[] {
    return this.memo.get('myTeams', [this.teams, this.currentUserId, this.currentUser.id],
      () => this.teams.filter((t: any) => t.teamLeadId === this.currentUserId || t.teamLeadId === this.currentUser.id));
  }

  get myTeamMemberIds(): string[] {
    return this.myTeamMembers.map((e: any) => e.id);
  }

  get myTeamMembers(): any[] {
    const managerId = this.currentUser.id || this.currentUserId;
    return this.memo.get('myTeamMembers', [this.employees, managerId],
      () => this.employees.filter((e: any) => e.managerId === managerId && this.isActiveEmp(e)));
  }

  get teamSize(): number { return this.myTeamMembers.length; }

  get teamPresentToday(): number {
    return new Set(
      this.attendance
        .filter((a: any) => this.myTeamMemberIds.includes(a.employeeId) && (a.checkIn || a.status === 'present'))
        .map((a: any) => a.employeeId)
    ).size;
  }

  get teamOnLeave(): number {
    const today = this.localDateStr(new Date());
    return new Set(
      this.leaves.filter((l: any) =>
        this.myTeamMemberIds.includes(l.employeeId) &&
        l.status === 'approved' && l.startDate <= today && l.endDate >= today
      ).map((l: any) => l.employeeId)
    ).size;
  }

  get teamLate(): number {
    return new Set(
      this.attendance.filter((a: any) => {
        if (!this.myTeamMemberIds.includes(a.employeeId) || !a.checkIn) return false;
        const t = this.normalizeTime(a.checkIn);
        const p = t.split(':');
        return (+p[0]) * 60 + (+p[1] || 0) > this.lateThresholdMinutes;
      }).map((a: any) => a.employeeId)
    ).size;
  }

  get teamAbsent(): number {
    return Math.max(0, this.teamSize - this.teamPresentToday - this.teamOnLeave);
  }

  get teamNeedsAttention(): number { return this.teamAbsent + this.teamLate; }

  get teamPendingLeaves(): any[] {
    return this.leaves.filter((l: any) =>
      this.myTeamMemberIds.includes(l.employeeId) && l.status === 'pending'
      && this.isEmployeeActiveById(l.employeeId)
    ).slice(0, 5);
  }

  teamRegularisations: any[] = [];
  get teamPendingRegularisations(): any[] {
    return this.teamRegularisations.filter((r: any) =>
      this.myTeamMemberIds.includes(r.employeeId)
      && (r.status || '').toLowerCase() === 'pending'
      && this.isEmployeeActiveById(r.employeeId)
    ).slice(0, 5);
  }

  private loadTeamRegularisations(): void {
    this.apiService.get('/attendance-regularise').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => { this.teamRegularisations = this.extractList(res); },
      error: () => { this.teamRegularisations = []; },
    });
  }

  approveRegularisation(r: any): void {
    this.apiService.patch('/attendance-regularise/' + r.id, { status: 'approved' })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { r.status = 'approved'; this.snackbarService.showSuccess('Regularisation approved'); },
        error: () => this.snackbarService.showFailure('Could not approve'),
      });
  }

  rejectRegularisation(r: any): void {
    this.apiService.patch('/attendance-regularise/' + r.id, { status: 'rejected' })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { r.status = 'rejected'; this.snackbarService.showSuccess('Regularisation rejected'); },
        error: () => this.snackbarService.showFailure('Could not reject'),
      });
  }

  get teamUpcomingLeaves(): any[] {
    const today = this.localDateStr(new Date());
    const twoWeeks = this.localDateStr(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
    return this.leaves.filter((l: any) =>
      this.myTeamMemberIds.includes(l.employeeId) &&
      (l.status === 'approved' || l.status === 'pending') &&
      l.startDate >= today && l.startDate <= twoWeeks
    ).slice(0, 5);
  }

  get teamLiveStatus(): { member: any, status: string, statusClass: string, detail: string, isLead: boolean }[] {
    const today = this.localDateStr(new Date());
    return this.memo.get('teamLiveStatus',
      [this.myTeamMembers, this.attendance, this.leaves, this.lateThresholdMinutes, today],
      () => this.myTeamMembers.map(member => {
      const recs = this.attendance.filter((a: any) => a.employeeId === member.id);
      const activeRec = recs.find((r: any) => r.checkIn && !r.checkOut) || recs.find((r: any) => r.checkIn);
      const onLeave = this.leaves.find((l: any) =>
        l.employeeId === member.id && l.status === 'approved' &&
        l.startDate <= today && l.endDate >= today
      );
      const isLead = this.myTeam?.teamLeadId === member.id;

      if (onLeave) {
        return { member, status: 'On Leave', statusClass: 'status-leave',
                 detail: (onLeave.leaveType || 'Leave') + ' · Approved · ' + onLeave.startDate + ' – ' + onLeave.endDate, isLead };
      }
      if (activeRec?.checkIn) {
        const t = this.normalizeTime(activeRec.checkIn);
        const p = t.split(':');
        const ciMins = (+p[0]) * 60 + (+p[1] || 0);
        const isLate = ciMins > this.lateThresholdMinutes;
        const minsLate = ciMins - (this.lateThresholdMinutes);
        const displayTime = this.formatUtcTime12(activeRec.checkIn, activeRec.date);
        if (isLate) {
          return { member, status: 'Late', statusClass: 'status-late',
                   detail: `Checked in ${displayTime} · ${minsLate} min late`, isLead };
        }
        return { member, status: 'On Time', statusClass: 'status-ontime',
                 detail: `In ${displayTime} · ${this.getAttendanceHours(activeRec)} worked`, isLead };
      }
      return { member, status: 'Absent', statusClass: 'status-absent',
               detail: 'No check-in · No leave on record', isLead };
    }));
  }

  get teamLiveStatusSorted(): { member: any, status: string, statusClass: string, detail: string, isLead: boolean }[] {
    return this.memo.get('teamLiveSorted', [this.teamLiveStatus], () => {
      const rank: Record<string, number> = { 'Absent': 0, 'Late': 1, 'On Time': 2, 'On Leave': 3 };
      return [...this.teamLiveStatus].sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9));
    });
  }

  // Live Status chips act as filters on the list; clicking the active chip clears it.
  teamLiveFilter: '' | 'On Time' | 'Late' | 'Absent' | 'On Leave' = '';
  setTeamLiveFilter(f: '' | 'On Time' | 'Late' | 'Absent' | 'On Leave'): void {
    this.teamLiveFilter = this.teamLiveFilter === f ? '' : f;
  }
  get teamLiveVisible(): { member: any, status: string, statusClass: string, detail: string, isLead: boolean }[] {
    const all = this.teamLiveStatusSorted;
    return this.memo.get('teamLiveVisible', [all, this.teamLiveFilter],
      () => this.teamLiveFilter ? all.filter(s => s.status === this.teamLiveFilter) : all);
  }

  // Work Hours Today: who is still on the clock vs done for the day.
  get teamHoursToday(): { member: any, hours: string, working: boolean }[] {
    return this.memo.get('teamHoursToday', [this.myTeamMembers, this.attendance], () => this.myTeamMembers
      .map(member => {
        const recs = this.attendance.filter((a: any) => a.employeeId === member.id);
        const rec = recs.find((r: any) => r.checkIn && !r.checkOut) || recs.find((r: any) => r.checkIn);
        if (!rec) return null;
        return { member, hours: this.getAttendanceHours(rec) || '—', working: !rec.checkOut };
      })
      .filter((x): x is { member: any, hours: string, working: boolean } => !!x)
      .sort((a, b) => Number(b.working) - Number(a.working)));
  }
  get teamStillWorkingCount(): number { return this.teamHoursToday.filter(x => x.working).length; }
  get teamCheckedOutCount(): number { return this.teamHoursToday.filter(x => !x.working).length; }

  get teamOnTimeCount(): number { return this.teamLiveStatus.filter(s => s.status === 'On Time').length; }

  get teamNeedsAttentionList(): { member: any, status: string, statusClass: string, detail: string, isLead: boolean }[] {
    return this.teamLiveStatus.filter(s => s.status === 'Absent' || s.status === 'Late');
  }

  get teamPresentList(): { member: any, status: string, statusClass: string, detail: string, isLead: boolean }[] {
    return this.teamLiveStatus.filter(s => s.status === 'On Time' || s.status === 'On Leave');
  }

  get teamLateList(): { member: any, status: string, statusClass: string, detail: string, isLead: boolean }[] {
    return this.teamLiveStatus.filter(s => s.status === 'Late').slice(0, 3);
  }

  get weeklyDayHeaders(): { label: string, dayNum: number }[] {
    return this.managerWeeklyRaw.map(day => ({
      label: day.date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase().slice(0, 3),
      dayNum: day.date.getDate()
    }));
  }

  get teamWeeklyRows(): { member: any, days: { label: string, dayNum: number, cls: string }[] }[] {
    if (!this.managerWeeklyRaw.length) return [];
    const today = this.localDateStr(new Date());
    return this.myTeamMembers.slice(0, 4).map(member => ({
      member,
      days: this.managerWeeklyRaw.map(day => {
        const recs = day.records.filter((r: any) => r.employeeId === member.id);
        const hasPresent = recs.some((r: any) => r.checkIn || r.status === 'present');
        const hasLeave = this.leaves.some((l: any) =>
          l.employeeId === member.id && l.status === 'approved' &&
          l.startDate <= day.dateStr && l.endDate >= day.dateStr
        );
        const isLateDay = recs.some((r: any) => {
          if (!r.checkIn) return false;
          const p = this.normalizeTime(r.checkIn).split(':');
          return (+p[0]) * 60 + (+p[1] || 0) > this.lateThresholdMinutes;
        });
        const isFuture = day.dateStr > today;
        let label = '—', cls = 'td-dash';
        if (isFuture) { label = '—'; cls = 'td-future'; }
        else if (hasLeave) { label = 'LV'; cls = 'td-leave'; }
        else if (hasPresent && isLateDay) { label = 'L'; cls = 'td-late'; }
        else if (hasPresent) { label = 'P'; cls = 'td-present'; }
        else { label = 'A'; cls = 'td-absent'; }
        return { label, dayNum: day.date.getDate(), cls };
      })
    }));
  }

  get shiftCoverageToday(): { shift: any, assigned: number, present: number, pct: number, absentName: string }[] {
  const memberIds = this.myTeamMemberIds;
  if (!memberIds.length) return [];

  const shiftMemberMap = new Map<string, string[]>();

  for (const memberId of memberIds) {
    const rosterEntry = this.rosterAssignments.find((r: any) => r.employeeId === memberId);
    let shiftId: string | null = null;

    if (rosterEntry?.shiftId) {
      shiftId = rosterEntry.shiftId;
    } else if (this.myTeam?.defaultShiftId) {
      shiftId = this.myTeam.defaultShiftId;
    }

    if (!shiftId) continue;

    if (!shiftMemberMap.has(shiftId)) {
      shiftMemberMap.set(shiftId, []);
    }
    shiftMemberMap.get(shiftId)!.push(memberId);
  }

  const result: any[] = [];

  shiftMemberMap.forEach((assignedMemberIds, shiftId) => {
    const shift = this.shifts.find((s: any) => s.id === shiftId);
    if (!shift) return;

    const presentIds = new Set(this.attendance
        .filter((a: any) =>
          assignedMemberIds.includes(a.employeeId) && (a.checkIn || a.status === 'present'))
        .map((a: any) => a.employeeId));
    const absentEmp = this.employees.find((e: any) =>
      assignedMemberIds.includes(e.id) && !presentIds.has(e.id));

    result.push({
      shift,
      assigned: assignedMemberIds.length,
      present: presentIds.size,
      pct: Math.round((presentIds.size / assignedMemberIds.length) * 100),
      absentName: absentEmp ? `${absentEmp.firstName} ${absentEmp.lastName}` : ''
    });
  });

  return result;
}

  showApprovalModal = false;
  approvalAction: 'approved' | 'rejected' = 'approved';
  approvalLeaveId = '';
  approvalLeave: any = null;
  approvalNote = '';
  approvalSubmitting = false;

  approveLeave(lv: any): void {
    this.approvalLeave   = lv;
    this.approvalLeaveId = lv.id;
    this.approvalAction  = 'approved';
    this.approvalNote    = '';
    this.showApprovalModal = true;
  }

  rejectLeave(lv: any): void {
    this.approvalLeave   = lv;
    this.approvalLeaveId = lv.id;
    this.approvalAction  = 'rejected';
    this.approvalNote    = '';
    this.showApprovalModal = true;
  }

  confirmApproval(): void {
    if (this.approvalAction === 'rejected' && !this.approvalNote.trim()) return;
    this.approvalSubmitting = true;
    this.apiService.patch('/leaves/' + this.approvalLeaveId, {
      status: this.approvalAction,
      approvedBy: this.currentUserId,
      comments: this.approvalNote.trim()
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        const leave = this.leaves.find(l => l.id === this.approvalLeaveId);
        if (leave) leave.status = this.approvalAction;
        this.snackbarService.showSuccess(this.approvalAction === 'approved' ? 'Leave approved' : 'Leave rejected');
        this.showApprovalModal = false;
        this.approvalSubmitting = false;
      },
      error: () => {
        this.snackbarService.showFailure('Failed to update leave. Please try again.');
        this.approvalSubmitting = false;
      }
    });
  }

  get myDaysPresent(): number {
    const now = new Date();
    const seenDates = new Set<string>();
  
  return this.monthlyAttendance.filter((a: any) => {
    if (a.employeeId !== this.currentUserId && a.employeeId !== this.currentUser.id) return false;
    if (!a.date) return false;
    const d = new Date(a.date);
    if (d.getFullYear() !== this.calendarDate.getFullYear() ||
        d.getMonth() !== this.calendarDate.getMonth()) return false;
    if (!(a.checkIn || a.status === 'present')) return false;
    
    const dateKey = a.date.split('T')[0];
    if (seenDates.has(dateKey)) return false;
    seenDates.add(dateKey);
    return true;
    }).length;
  }

  get myTotalLeaveBalance(): number {
    return this.getMyLeaveBalance('casual') + this.getMyLeaveBalance('sick') + this.getMyLeaveBalance('earned');
  }

  get myPendingLeaveCount(): number {
    return this.myLeaves.filter((l: any) => l.status === 'pending').length;
  }

  getMyLeaveBalance(type: string): number {
    const balances = this.leaveBalances.filter((b: any) =>
      b.leaveType === type && (b.employeeId === this.currentUserId || b.employeeId === this.currentUser.id || b.employeeId === this.currentUser.employeeId));
    if (!balances.length) return 0;
    const total = balances.reduce((sum: number, b: any) => sum + (b.totalDays || 0), 0);
    const used  = balances.reduce((sum: number, b: any) => sum + (b.usedDays  || 0), 0);
    return Math.max(0, total - used);
  }

  get myLeaveBalanceSummary(): { type: string; label: string; remaining: number }[] {
    const myBalances = this.leaveBalances.filter((b: any) =>
      b.employeeId === this.currentUserId || b.employeeId === this.currentUser.id || b.employeeId === this.currentUser.employeeId
    );
    const typeMap = new Map<string, { total: number; used: number }>();
    for (const b of myBalances) {
      const key = b.leaveType as string;
      const cur = typeMap.get(key) ?? { total: 0, used: 0 };
      typeMap.set(key, { total: cur.total + (b.totalDays || 0), used: cur.used + (b.usedDays || 0) });
    }
    const abbr: Record<string, string> = { casual: 'CL', sick: 'SL', earned: 'EL', maternity: 'ML', paternity: 'PL', compensatory: 'Comp' };
    return Array.from(typeMap.entries())
      .map(([type, { total, used }]) => ({
        type,
        label: abbr[type.toLowerCase()] ?? type.slice(0, 3).toUpperCase(),
        remaining: Math.max(0, total - used)
      }))
      .filter(t => t.remaining > 0 || typeMap.has(t.type));
  }

  getMyDaysPresent(): number {
    return this.attendance.filter(a => a.employeeId === this.currentUserId && a.status === 'present').length;
  }

  getWorkingDaysInMonth(): number {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const day = new Date(year, month, d).getDay();
      if (day !== 0 && day !== 6) count++;
    }
    return count;
  }

  getMyShift(): string {
    const assignment = this.rosterAssignments.find((r: any) => r.employeeId === this.currentUserId);
    if (assignment) {
      const shift = this.shifts.find((s: any) => s.id === assignment.shiftId);
      return shift ? shift.name : 'General';
    }
    return 'General';
  }
  getMyShiftTime(): string {
    const assignment = this.rosterAssignments.find((r: any) => r.employeeId === this.currentUserId);
    if (assignment) {
      const shift = this.shifts.find((s: any) => s.id === assignment.shiftId);
      const fallback = (this.companySettings.defaultShiftStart ?? '09:00') + ' – ' + (this.companySettings.defaultShiftEnd ?? '18:00');
      return shift ? (shift.startTime + ' – ' + shift.endTime) : fallback;
    }
    return (this.companySettings.defaultShiftStart ?? '09:00') + ' – ' + (this.companySettings.defaultShiftEnd ?? '18:00');
  }

  loadMyManager(): void {
    this.apiService.get('/employees/my-manager').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const mgr = res?.data ?? res;
        if (mgr && mgr.firstName) {
          this.setMyManager(mgr);
        } else {
          this.loadMyManagerFallback();
        }
      },
      error: () => this.loadMyManagerFallback()
    });
  }

  private loadMyManagerFallback(): void {
    const userId = this.currentUser.id || this.currentUser.employeeId;
    if (!userId) return;
    this.apiService.get('/employees/' + userId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const emp = res?.data ?? res;
        const managerId = emp?.managerId;
        if (!managerId) {
          this.resolveManagerFromList();
          return;
        }
        this.apiService.get('/employees/' + managerId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (r: any) => { const m = r?.data ?? r; if (m?.firstName) this.setMyManager(m); },
          error: () => this.resolveManagerFromList()
        });
      },
      error: () => this.resolveManagerFromList()
    });
  }

  private resolveManagerFromList(): void {
    const me = this.employees.find((e: any) =>
      e.id === this.currentUser.id || e.email === this.currentUser.email
    );
    const managerId = me?.managerId || this.currentUser.managerId;
    if (!managerId) return;
    const mgr = this.employees.find((e: any) => e.id === managerId);
    if (mgr?.firstName) this.setMyManager(mgr);
  }

  private setMyManager(mgr: any): void {
    const name = ((mgr.firstName || '') + ' ' + (mgr.lastName || '')).trim();
    const initials = ((mgr.firstName?.charAt(0) || '') + (mgr.lastName?.charAt(0) || '')).toUpperCase();
    this.myManager = {
      name,
      employeeId: mgr.employeeId || '',
      role: mgr.designation || mgr.role || 'Manager',
      photo: mgr.profilePhoto || mgr.photo || null,
      initials: initials || '?',
      avatarColor: this.getAvatarColor(mgr.firstName)
    };
  }

  getMyManager(): any {
    const build = (e: any) => ({
      name: e.firstName + ' ' + e.lastName,
      employeeId: e.employeeId || '',
      role: e.designation || e.role || 'Manager',
      photo: e.profilePhoto || e.photo || e.avatarUrl || null,
      initials: ((e.firstName?.charAt(0) || '') + (e.lastName?.charAt(0) || '')).toUpperCase(),
      avatarColor: this.getAvatarColor(e.firstName)
    });
    const me = this.employees.find(e =>
      e.id === this.currentUser.id ||
      e.id === this.currentUser.employeeId ||
      e.email === this.currentUser.email
    );
    const managerId = me?.managerId || this.currentUser.managerId;
    if (managerId) {
      const mgr = this.employees.find(e => e.id === managerId);
      if (mgr) return build(mgr);
    }
    return null;
  }

  calculateWorkHours(): string {
    if (!this.myAttendance?.checkIn) return '0H 00M';
    const ciStr = this.normalizeTime(this.myAttendance.checkIn);
    const parts = ciStr.split(':');
    if (parts.length < 2) return '0H 00M';
    const checkInH = +parts[0]; const checkInM = +parts[1] || 0;
    let nowH: number, nowM: number;
    if (this.myAttendance.checkOut) {
      const coStr = this.normalizeTime(this.myAttendance.checkOut);
      const coParts = coStr.split(':');
      nowH = +coParts[0]; nowM = +coParts[1] || 0;
    } else {
      nowH = new Date().getHours(); nowM = new Date().getMinutes();
    }
    let diffM = (nowH * 60 + nowM) - (checkInH * 60 + checkInM);
    if (diffM < 0) diffM = 0;
    return `${Math.floor(diffM / 60)}H ${(diffM % 60).toString().padStart(2, '0')}M`;
  }

  get workPercent(): number {
    if (!this.myAttendance?.checkIn) return 0;
    const ciStr = this.normalizeTime(this.myAttendance.checkIn);
    const parts = ciStr.split(':');
    const ci = new Date();
    ci.setHours(+parts[0], +parts[1] || 0, +parts[2] || 0, 0);
    const end = this.myAttendance.checkOut ? (() => {
      const coStr = this.normalizeTime(this.myAttendance.checkOut);
      const coParts = coStr.split(':');
      const d = new Date(); d.setHours(+coParts[0], +coParts[1] || 0, +coParts[2] || 0, 0); return d;
    })() : new Date();
    const diff = Math.max(0, (end.getTime() - ci.getTime()) / 1000);
    return Math.min(100, Math.round((diff / (8 * 3600)) * 100));
  }

  get checkInTime(): string {
    if (!this.myAttendance?.checkIn) return '';
    return this.formatTime12(this.normalizeTime(this.myAttendance.checkIn));
  }

  get checkOutTime(): string {
    if (!this.myAttendance?.checkOut) return '';
    return this.formatTime12(this.normalizeTime(this.myAttendance.checkOut));
  }

  get totalWorked(): string {
    if (!this.myAttendance?.checkIn || !this.myAttendance?.checkOut) return '00:00:00';
    const ciStr = this.normalizeTime(this.myAttendance.checkIn);
    const coStr = this.normalizeTime(this.myAttendance.checkOut);
    const ciParts = ciStr.split(':');
    const coParts = coStr.split(':');
    const ci = new Date(); ci.setHours(+ciParts[0], +ciParts[1] || 0, +ciParts[2] || 0, 0);
    const co = new Date(); co.setHours(+coParts[0], +coParts[1] || 0, +coParts[2] || 0, 0);
    const diff = Math.max(0, Math.floor((co.getTime() - ci.getTime()) / 1000));
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
  }

  async checkIn(): Promise<void> {
    this.checkingIn = true;
    const now = new Date();
    const time = now.toTimeString().split(' ')[0];

    let isLate = false;
    if (this.myShift && this.myShift.startTime) {
      isLate = this.checkIfLate(time, this.myShift.startTime);
    }

    const loc = await this.getLocationOrNull();

    if (!loc.latitude) {
      this.checkingIn = false;
      this.snackbarService.showFailure(
        loc.reason || 'We need your location to check you in. Please allow location access and try again.');
      return;
    }

    const payload: any = {
      employeeId:  this.currentUserId,
      latitude:    loc.latitude,
      longitude:   loc.longitude,
      accuracy:    loc.accuracy,
      checkInMode: 'Mobile',
      notes:       'present'
    };

    this.apiService.post('/attendance/check-in', payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.myAttendance = data;
        this.isLateCheckIn = isLate;
        this.checkingIn = false;
        if (data?.id) this.timerService.startSession(data.id, now, 0);
        this.loadDashboardData();
      },
      error: (err: any) => {
        this.checkingIn = false;
        const rawMsg: string = (err?.error?.message || err?.error?.error || '').trim();
        const msg = rawMsg.toLowerCase();

        if (msg.includes('already') || err?.status === 409) {
          this.snackbarService.showInfo('Already checked in — syncing latest state');
          this.loadDashboardData();
        } else if (msg.includes('outside') || msg.includes('geofence') || msg.includes('zone')
                || msg.includes('radius') || msg.includes('location')) {
          this.snackbarService.showFailure(rawMsg || 'You are outside the allowed check-in zone.');
        } else {
          this.snackbarService.showFailure('Check-in failed. Please try again.');
        }
      }
    });
  }

  private getLocationOrNull(): Promise<{ latitude: number | null; longitude: number | null; accuracy: number | null; reason: string }> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null, accuracy: null, reason: 'This device/browser does not support location.' });
        return;
      }
      if (!window.isSecureContext) {
        resolve({ latitude: null, longitude: null, accuracy: null, reason: 'Location needs a secure (https) connection. Please open the site via its https address.' });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy, reason: '' }),
        (err) => resolve({
          latitude: null, longitude: null, accuracy: null,
          reason: err.code === err.PERMISSION_DENIED ? 'Location permission was blocked. Allow location for this site and try again.'
                : err.code === err.POSITION_UNAVAILABLE ? 'Your location could not be determined. Move near a window or enable GPS, then retry.'
                : err.code === err.TIMEOUT ? 'Getting your location took too long. Please try again.'
                : 'Could not read your location.',
        }),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    });
  }

  private checkIfLate(checkInTime: string, shiftStartTime: string): boolean {
    const ciParts = checkInTime.split(':');
    const shiftParts = shiftStartTime.split(':');
    const ciMinutes = (+ciParts[0]) * 60 + (+ciParts[1] || 0);
    const shiftMinutes = (+shiftParts[0]) * 60 + (+shiftParts[1] || 0);
    return ciMinutes > (shiftMinutes + (this.companySettings?.gracePeriodMinutes ?? 15));
  }

  checkOut(): void {
    if (!this.myAttendance?.id) return;
    this.confirmShortCheckout(this.myAttendance.checkIn, () => this.doCheckOut());
  }

  // Accidental-checkout guard: a check-out within 30 minutes of checking in is almost always
  // a double-click/mis-tap — confirm before recording it.
  private confirmShortCheckout(checkInTime: string | null | undefined, proceed: () => void): void {
    let mins = 999;
    const m = /^(\d{1,2}):(\d{2})/.exec(String(checkInTime || '').includes('T')
      ? String(checkInTime).split('T')[1] : String(checkInTime || ''));
    if (m) {
      const now = new Date();
      mins = (now.getHours() * 60 + now.getMinutes()) - ((+m[1]) * 60 + (+m[2]));
    }
    if (mins >= 0 && mins < 30) {
      this.confirmDialog.confirm(
        `You checked in only ${mins <= 1 ? 'a minute' : mins + ' minutes'} ago — check out anyway?`,
        { title: 'Confirm Check Out', confirmLabel: 'Check Out', danger: true },
      ).then(ok => { if (ok) proceed(); });
      return;
    }
    proceed();
  }

  private doCheckOut(): void {
    if (!this.myAttendance?.id) return;
    this.checkingOut = true;
    const now = new Date();
    const time = now.toTimeString().split(' ')[0];
    this.apiService.patch('/attendance/' + this.myAttendance.id, { checkOut: time }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.myAttendance = data;
        this.checkingOut = false;
        this.timerService.endSession();
        this.loadDashboardData();
      },
      error: () => {
        if (this.myAttendance) this.myAttendance.checkOut = time;
        this.checkingOut = false;
        this.timerService.endSession();
      }
    });
  }

  buildCalendar(): void {
    const d = this.calendarDate;
    this.calendarMonth = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const year = d.getFullYear(); const month = d.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevDays = new Date(year, month, 0).getDate();
    const cells: any[] = [];
    for (let i = firstDay - 1; i >= 0; i--) cells.push({ day: prevDays - i, current: false, status: '' });
    const todayStr = this.localDateStr(new Date());
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
      cells.push({ day: i, current: true, isToday: dateStr === todayStr, status: this.getDayStatus(dateStr) });
    }
    let nextDay = 1;
    while (cells.length % 7 !== 0) cells.push({ day: nextDay++, current: false, status: '' });
    this.calendarWeeks = [];
    for (let i = 0; i < cells.length; i += 7) this.calendarWeeks.push(cells.slice(i, i + 7));
  }

  prevMonth(): void { this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() - 1, 1); this.buildCalendar(); this.calSelectedDate = null; this.calSelectedRecord = null; this.calSelectedStatus = ''; }
  nextMonth(): void { this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + 1, 1); this.buildCalendar(); this.calSelectedDate = null; this.calSelectedRecord = null; this.calSelectedStatus = ''; }

  selectCalDay(cell: any): void {
    if (!cell.current) return;
    const d = this.calendarDate;
    const dateStr = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${cell.day.toString().padStart(2, '0')}`;
    if (this.calSelectedDate === dateStr) {
      this.calSelectedDate = null; this.calSelectedRecord = null;
      this.calSelectedStatus = ''; this.calCheckInLocation = ''; this.calCheckOutLocation = '';
      return;
    }
    this.calSelectedDate = dateStr;
    this.calSelectedStatus = cell.status || '';
    this.calCheckInLocation = ''; this.calCheckOutLocation = '';
    this.calSelectedRecord = this.monthlyAttendance.find((a: any) => {
      const recDate = (a.date || a.attendanceDate || '').split('T')[0];
      return recDate === dateStr && (a.employeeId === this.currentUserId || a.employee?.id === this.currentUserId);
    }) || null;

    if (this.calSelectedRecord) {
      const ciLat = this.calSelectedRecord.latitude    || this.calSelectedRecord.checkInLat;
      const ciLng = this.calSelectedRecord.longitude   || this.calSelectedRecord.checkInLng || this.calSelectedRecord.checkInLon;
      const coLat = this.calSelectedRecord.checkOutLat;
      const coLng = this.calSelectedRecord.checkOutLng || this.calSelectedRecord.checkOutLon;
      if (ciLat && ciLng) {
        this.http.get<any>(
          `https://nominatim.openstreetmap.org/reverse?lat=${ciLat}&lon=${ciLng}&format=json`,
          { headers: { 'Accept-Language': 'en' } }
        ).pipe(catchError(() => of(null))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
          if (res?.display_name) {
            const parts = res.display_name.split(',');
            this.calCheckInLocation = parts.slice(0, 3).join(', ').trim();
          }
        });
      }
      if (coLat && coLng) {
        this.http.get<any>(
          `https://nominatim.openstreetmap.org/reverse?lat=${coLat}&lon=${coLng}&format=json`,
          { headers: { 'Accept-Language': 'en' } }
        ).pipe(catchError(() => of(null))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
          if (res?.display_name) {
            const parts = res.display_name.split(',');
            this.calCheckOutLocation = parts.slice(0, 3).join(', ').trim();
          }
        });
      }
    }
  }

  formatTime(t: string | null | undefined, date?: any): string {
    if (!t) return '—';
    return this.formatUtcTime12(t, date);
  }

  isBeforeDOJ(dateStr: string): boolean {
  const emp = this.employees.find(
    (e) => e.id === this.currentUserId || e.employeeId === this.currentUserId
  );
  return emp?.dateOfJoining && dateStr < emp.dateOfJoining;
  }
  
private getDayStatus(dateStr: string): string {
  const today = this.localDateStr(new Date());
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay();

  if (this.holidays && this.holidays[dateStr]) return 'holiday';

  if (dayOfWeek === 0 || dayOfWeek === 6) return 'weekend';

  if (this.isBeforeDOJ(dateStr)) return 'before-doj';

  const leave = this.myLeaves.find((l: any) =>
    l.status === 'approved' &&
    l.startDate <= dateStr &&
    l.endDate >= dateStr
  );
  if (leave) return 'leave';

  if (dateStr > today) return 'future';


  const att = this.monthlyAttendance.find((a: any) => {
    const attDate = (a.date || '').split('T')[0];
    return attDate === dateStr && (
      a.employeeId === this.currentUserId ||
      a.employeeId === this.currentUser.id
    );
  });


  if (att && (att.checkIn || att.status === 'present')) return 'present';

  if (dateStr < today) return 'absent';

  return 'no-checkin';
}

  editTeam(team: any): void {
    this.teamForm = {
      id: team.id,
      name: team.name,
      teamLeadId: team.teamLeadId,
      departmentId: team.departmentId
    };
    this.editingTeam = true;
    this.showTeamDialog = true;
  }

  closeTeamDialog(): void {
    this.showTeamDialog = false;
    this.editingTeam = false;
    this.teamForm = { id: '', name: '', teamLeadId: '', departmentId: '' };
  }

  saveTeam(): void {
    if (!this.teamForm.name || !this.teamForm.teamLeadId) return;
    
    const payload = {
      name: this.teamForm.name,
      teamLeadId: this.teamForm.teamLeadId,
      departmentId: this.teamForm.departmentId
    };
    
    if (this.editingTeam && this.teamForm.id) {
      this.apiService.patch('/teams/' + this.teamForm.id, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.closeTeamDialog();
          this.loadDashboardData();
        },
        error: (err) => console.error('Error updating team:', err)
      });
    } else {
      this.apiService.post('/teams', payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.closeTeamDialog();
          this.loadDashboardData();
        },
        error: (err) => console.error('Error creating team:', err)
      });
    }
  }

  getEmployeeName(id: string): string {
    const emp = this.employees.find(e => e.id === id || e.employeeId === id || e.userId === id);
    return emp ? emp.firstName + ' ' + emp.lastName : 'Unknown';
  }

  getEmployeeInitials(id: string): string {
    const emp = this.employees.find(e => e.id === id || e.employeeId === id || e.userId === id);
    return emp ? ((emp.firstName?.charAt(0) || '') + (emp.lastName?.charAt(0) || '')).toUpperCase() : '??';
  }

  getDeptName(id: string): string {
    const d = this.departments.find(dept => dept.id === id || dept.departmentId === id);
    return d ? d.name : '';
  }

  getLeaveDays(leave: any): number {
    if (leave.noOfDays) return leave.noOfDays;
    if (leave.totalDays) return leave.totalDays;
    if (leave.days) return leave.days;
    if (leave.startDate && leave.endDate) {
      const start = new Date(leave.startDate);
      const end   = new Date(leave.endDate);
      const diff  = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
      return diff > 0 ? diff : 1;
    }
    return 0;
  }

  private loadAllTeamMembers(): void {
    if (this.teams.length === 0) return;
    const requests = this.teams.map(t =>
      this.apiService.get('/teams/' + t.id + '/members').pipe(catchError(() => of([])))
    );
    forkJoin(requests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (responses: any[]) => {
        const map: { [teamId: string]: any[] } = {};
        responses.forEach((res, i) => {
          const members = this.extractList(res);
          map[this.teams[i].id] = members
            .filter((m: any) => m?.employeeId)
            .map((m: any) => this.employees.find((e: any) => e.id === m.employeeId))
            .filter(Boolean);
        });
        this.teamMembersMap = map;
      },
      error: () => {}
    });
  }

  getDeptEmployeeCount(deptId: string): number {
    const fromDeptField = this.employees.filter((e: any) => e.departmentId === deptId && this.isActiveEmp(e)).map((e: any) => e.id);
    const deptTeams = this.teams.filter((t: any) => t.departmentId === deptId);
    const fromTeams = deptTeams.flatMap(t => (this.teamMembersMap[t.id] || []).map((e: any) => e?.id).filter(Boolean));
    return new Set([...fromDeptField, ...fromTeams]).size;
  }

  isBirthdayToday(emp: any): boolean {
    if (!emp.dateOfBirth) return false;
    const today = new Date();
    const dob = new Date(emp.dateOfBirth);
    return dob.getMonth() === today.getMonth() && dob.getDate() === today.getDate();
  }

  get todayBirthdays(): any[] {
    return this.activeEmployees.filter(e => this.isBirthdayToday(e));
  }

  isBirthdayThisWeek(emp: any): boolean {
    if (!emp.dateOfBirth) return false;
    const today = new Date();
    const dob = new Date(emp.dateOfBirth);
    for (let i = 0; i < 7; i++) {
      const d = new Date(today); d.setDate(today.getDate() + i);
      if (dob.getMonth() === d.getMonth() && dob.getDate() === d.getDate()) return true;
    }
    return false;
  }

  isWorkAnniversaryThisWeek(emp: any): boolean {
    if (!emp.joiningDate) return false;
    const today = new Date();
    const joined = new Date(emp.joiningDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(today); d.setDate(today.getDate() + i);
      if (joined.getMonth() === d.getMonth() && joined.getDate() === d.getDate() && joined.getFullYear() < d.getFullYear()) return true;
    }
    return false;
  }

  getWorkAnniversaryYears(emp: any): number {
    if (!emp.joiningDate) return 0;
    return new Date().getFullYear() - new Date(emp.joiningDate).getFullYear();
  }

  hasCelebrations(): boolean {
    return this.activeEmployees.some(e => this.isBirthdayThisWeek(e) || this.isWorkAnniversaryThisWeek(e));
  }

  sendWish(emp: any, type: 'birthday' | 'anniversary'): void {
    const key = `${emp.id}-${type}`;
    if (this.wishedEmployees.has(key)) {
      this.snackbarService.showFailure(`You already wished ${emp.firstName}!`);
      return;
    }
    const name    = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
    const message = type === 'birthday'
      ? `Happy Birthday, ${name}! 🎂`
      : `Congratulations on your work anniversary, ${name}! 🎉`;
    this.apiService.post('/notifications/wish', {
      recipientId: emp.id,
      message,
      type,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next:  () => {},
      error: () => {},
    });
    this.wishedEmployees.add(key);
    this.snackbarService.showSuccess(message);
  }

  // Real onboarding checklist, keyed by employee — one entry per OnboardingTask row from
  // the actual onboarding module (see OnboardingController#getAll), not a guess.
  onboardingTasksByEmployee = new Map<string, any[]>();

  private tasksFor(emp: any): any[] {
    return this.onboardingTasksByEmployee.get(emp.id) ?? [];
  }

  /** True once we know this employee actually has a checklist assigned. */
  hasOnboardingChecklist(emp: any): boolean {
    return this.tasksFor(emp).length > 0;
  }

  getOnboardingProgress(emp: any): number {
    const tasks = this.tasksFor(emp);
    if (!tasks.length) return 0;
    const done = tasks.filter(t => t.status === 'COMPLETED').length;
    return Math.round((done / tasks.length) * 100);
  }

  /** Every task still open, in order — not just a single guessed line. */
  getOnboardingPendingTasks(emp: any): any[] {
    return this.tasksFor(emp).filter(t => t.status !== 'COMPLETED');
  }

  getOnboardingPending(emp: any): string {
    const tasks = this.tasksFor(emp);
    if (!tasks.length) return 'No checklist assigned yet';
    const pending = this.getOnboardingPendingTasks(emp);
    if (!pending.length) return 'All tasks complete';
    const extra = pending.length > 1 ? ` (+${pending.length - 1} more)` : '';
    return pending[0].title + extra;
  }

  openOnboardingFor(emp: any): void {
    this.navigateTo(`/onboarding/${emp.id}`);
  }

  getTeamName(empId: string): string {
    for (const t of this.teams) {
      if (t.memberIds && t.memberIds.includes(empId)) return t.name;
      if (t.teamLeadId === empId || t.teamLead?.id === empId) return t.name;
    }
    return '';
  }

  getAttendanceHours(a: any): string {
    if (!a.checkIn) return '—';
    const ciStr = this.normalizeTime(a.checkIn);
    const coStr = a.checkOut ? this.normalizeTime(a.checkOut) : '';
    if (!coStr) {
      const ciParts = ciStr.split(':');
      const ci = new Date(); ci.setHours(+ciParts[0], +ciParts[1] || 0, 0);
      const diff = Math.max(0, Math.floor((Date.now() - ci.getTime()) / 60000));
      return `${Math.floor(diff / 60)}h ${this.pad(diff % 60)}m`;
    }
    const cin = ciStr.split(':'); const cout = coStr.split(':');
    const diff = ((+cout[0]) * 60 + (+cout[1] || 0)) - ((+cin[0]) * 60 + (+cin[1] || 0));
    return diff > 0 ? `${Math.floor(diff / 60)}h ${this.pad(diff % 60)}m` : '—';
  }

  getAttendanceStatus(a: any): string {
    if (a.status === 'absent') return 'Absent';
    if (!a.checkIn) return 'Absent';
    const t = this.normalizeTime(a.checkIn);
    const parts = t.split(':');
    const isLate = (+parts[0]) * 60 + (+parts[1] || 0) > this.lateThresholdMinutes;
    if (isLate) return 'Late';
    const leave = this.leaves.find(l => l.employeeId === a.employeeId && l.status === 'approved' && this.isLeaveToday(l));
    if (leave) return 'Leave';
    return 'On Time';
  }

  getAvatarColor(name: string): string {
    const colors = ['#334155', '#475569', '#0f766e', '#166534', '#92400e', '#7c3a5a', '#3f4a5f', '#5b6472'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  }

  navigateTo(path: string, queryParams?: any): void {
    this.router.navigate([path], queryParams ? { queryParams } : {});
  }

  private isLeaveToday(l: any): boolean {
    const t = this.localDateStr(new Date());
    return l.startDate <= t && l.endDate >= t;
  }

  openEmployeeModal(member: any): void {
    this.modalEmployee = member;
    this.showEmployeeModal = true;
  }

  closeEmployeeModal(): void {
    this.showEmployeeModal = false;
    this.modalEmployee = null;
  }

  get modalEmployeeLeaves(): any[] {
    if (!this.modalEmployee) return [];
    return this.leaves
      .filter((l: any) => l.employeeId === this.modalEmployee.id)
      .sort((a: any, b: any) => (b.startDate || '').localeCompare(a.startDate || ''));
  }

  get modalEmployeeLeaveStats(): { approved: number, pending: number, rejected: number } {
    const l = this.modalEmployeeLeaves;
    return {
      approved: l.filter((x: any) => x.status === 'approved').length,
      pending: l.filter((x: any) => x.status === 'pending').length,
      rejected: l.filter((x: any) => x.status === 'rejected').length
    };
  }

  get modalEmployeeMonthlyAtt(): any[] {
    if (!this.modalEmployee) return [];
    const now = new Date();
    const yr = now.getFullYear(); const mo = now.getMonth();
    const source = this.monthlyAttendance.length > 0 ? this.monthlyAttendance : this.managerWeeklyRaw.flatMap(d => d.records);
    return source.filter((a: any) => {
      if (a.employeeId !== this.modalEmployee.id) return false;
      const d = new Date(a.date || a.checkIn || '');
      return d.getFullYear() === yr && d.getMonth() === mo;
    });
  }

  get modalEmployeeAttStats(): { present: number, late: number, absent: number, leave: number, workingDays: number } {
    const workingDays = this.getWorkingDaysInMonth();
    const seenDates = new Map<string, any>();
    this.modalEmployeeMonthlyAtt.forEach((a: any) => {
      const dk = (a.date || '').split('T')[0];
      if (dk && !seenDates.has(dk)) seenDates.set(dk, a);
    });
    let present = 0, late = 0;
    seenDates.forEach(a => {
      if (a.checkIn || a.status === 'present') {
        present++;
        const t = this.normalizeTime(a.checkIn || '');
        const p = t.split(':');
        if ((+p[0]) * 60 + (+p[1] || 0) > this.lateThresholdMinutes) late++;
      }
    });
    const leaveCount = new Set(
      this.modalEmployeeLeaves
        .filter((l: any) => l.status === 'approved')
        .map((l: any) => l.startDate)
    ).size;
    const absent = Math.max(0, workingDays - present - leaveCount);
    return { present, late, absent, leave: leaveCount, workingDays };
  }

  modalLeaveStatusClass(status: string): string {
    if (status === 'approved') return 'leave-approved';
    if (status === 'pending') return 'leave-pending';
    if (status === 'rejected') return 'leave-rejected';
    return '';
  }
  
  private stopWorkTimer() {
  if (this.workTimerInterval) {
    clearInterval(this.workTimerInterval);
    this.workTimerInterval = null;
  }
}
}
